// src/services/UserService.js
import axios from './api'

/**
 * Logs in a user with the provided credentials and handles token storage.
 * @param {string} email - User email address.
 * @param {string} password - User password.
 * @returns {Promise<Object>} The user data from the response.
 */
export const userLogin = async (email, password) => {
    try {
        const response = await axios.post(`/api/auth-token/`, {
            username: email,
            password: password
        })

        // Handle token storage and axios header updates here or in the component
        if (response.data.token) {
            localStorage.setItem('authToken', response.data.token)
            localStorage.setItem('isAuthenticated', true)
        }
        return response.data
    } catch (error) {
        console.error('Login failed:', error)
        throw new Error(error.response?.data?.non_field_errors?.[0] || 'Login failed')
    }
}

export const resendOTP = async (email) => {
    try {
        const response = await axios.post('/api/account/resend-otp/', { email })
        return response.data.message
    } catch (error) {
        throw new Error(error.response?.data?.message || 'Failed to resend activation code.')
    }
}

export const activateAccount = async (email, otp) => {
    try {
        const response = await axios.post('/api/account/activate/', { email, otp })
        return response.data.message
    } catch (error) {
        throw new Error(
            error.response?.data?.message || 'Account activation failed. Please try again.'
        )
    }
}

export const getUserData = async () => {
    const response = await axios.get(`/api/account/`)
    return response.data
}

/**
 * Updates writable fields on the authenticated user's profile.
 * @param {string} id - The user's UUID.
 * @param {Object} data - The fields to update (e.g. `{ name: 'Alice' }`).
 * @returns {Promise<Object>} The updated user data.
 */
export const updateUserProfile = async (id, data) => {
    const response = await axios.patch(`/api/users/${id}/`, data)
    return response.data
}

export const getUserRoles = async () => {
    const response = await axios.get('/api/account/roles/')
    return response.data
}

export const fetchUserPreferences = async () => {
    try {
        const response = await axios.get(`/api/account/preferences/`)
        // Ensure there's data and it's of expected structure/type
        if (response.data === null || typeof response.data !== 'object') {
            // Adjust based on your context
            throw new Error('Expected data not received.')
        }
        return response.data
    } catch (error) {
        // Re-throw specific errors directly related to data fetching
        if (error.message === 'Expected data not received.') {
            throw error
        }

        // Check if the error has a response attached to it, which Axios does if the error is from the server
        if (error.response) {
            // Optionally check for specific status codes if needed
            throw new Error('Failed to fetch user preferences.')
        } else {
            // This handles cases where the error might be due to network issues or other problems that prevent a response
            throw new Error('Network or setup error when fetching preferences.')
        }
    }
}

export const updateUserPreferences = async (preferences) => {
    try {
        const response = await axios.patch(`/api/account/preferences/`, preferences)
        if (!response.data) {
            throw new Error('Expected data not received.')
        }
        return response.data
    } catch (error) {
        // Check if the error has a response attached to it, which Axios does if the error is from the server
        if (error.response) {
            throw new Error(`Failed to update user preferences: ${error.message}`)
        } else {
            // This handles cases where the error might be due to network issues or other problems that prevent a response
            throw new Error(`Network or setup error when updating preferences. ${error.message}`)
        }
    }
}

export const userLogout = async () => {
    try {
        await axios.post(`/api/account/logout/`)
    } catch (error) {
        console.error('Failed to log out:', error)
        // Handle specific logout errors or perform actions based on the error
    } finally {
        // Ensure headers are cleared even if the logout request fails
        localStorage.removeItem('isAuthenticated')
        localStorage.removeItem('authToken')
        localStorage.removeItem('userData')
    }
}
